import { useEffect, useState } from 'react';

import { toArabicDigits, toMachineNumber } from '@shared/format';

import { AuthButton } from '../_shared/components';

import { copy } from './copy';

/**
 * The resend bar — the condition, not a decoration.
 *
 * A code cannot be re-requested while the previous one is still alive, so the
 * countdown *is* the rule and the button is disabled for its duration with the
 * reason written beside it. Hiding the button instead would leave an operator
 * who never received the message with nothing to look for.
 *
 * **Two digit sets on one element, and both are correct.** `data-otp-seconds`
 * carries ASCII because a script parses it; the visible count carries
 * Arabic-Indic because a person reads it. Rendering either the other way would
 * be wrong about one of them.
 *
 * **The count is not a live region.** Announcing it every second floods a
 * screen reader with a number nobody asked to hear ticking. The region beside
 * it speaks exactly once, at the moment the button's state actually changes.
 */
/** Seconds left until an instant, never negative. */
function secondsUntil(until: number): number {
  return Math.max(0, Math.ceil((until - Date.now()) / 1000));
}

export function ResendTimer({
  until,
  epoch,
  pending,
  online,
  onResend,
}: {
  /**
   * When the bar lifts, as an absolute epoch-millisecond instant.
   *
   * **A deadline rather than a duration, and the count is derived from it on
   * every tick rather than decremented.** A stored duration is only true at the
   * moment it was issued: reloading the page re-seeded the full wait while the
   * service's code carried on ageing, so the screen eventually told the operator
   * their code had expired and kept the resend button barred for another
   * twenty-five seconds. Reading the clock also survives a backgrounded tab,
   * where an interval that decrements a counter is throttled and drifts.
   */
  until: number;
  /**
   * Which bar this is. It exists because a resend usually returns the **same**
   * `retry_after` as the first request, and a bar keyed on the deadline alone
   * would not restart on the one press that must restart it.
   */
  epoch: number;
  pending: boolean;
  online: boolean;
  onResend: () => void;
}) {
  const [remaining, setRemaining] = useState(() => secondsUntil(until));
  const [announcement, setAnnouncement] = useState('');

  /**
   * One effect, keyed on which bar this is: a resend re-seeds from the new
   * `retry_after` and restarts the count in the same pass. Splitting the seed
   * from the tick would need a second effect keyed on whether the count is
   * running, which re-creates the interval on the tick that reaches zero.
   *
   * The first bar is the one the sign-in screen already opened, so its arrival
   * is not news; every later one is a press the operator made.
   */
  useEffect(() => {
    if (epoch > 0) setAnnouncement(copy.timer.resent);

    setRemaining(secondsUntil(until));
    if (secondsUntil(until) <= 0) return undefined;

    const handle = window.setInterval(() => {
      const left = secondsUntil(until);
      setRemaining(left);
      if (left <= 0) {
        window.clearInterval(handle);
        setAnnouncement(copy.timer.expired);
      }
    }, 1000);

    return () => window.clearInterval(handle);
  }, [until, epoch]);

  const barred = remaining > 0;

  return (
    <div data-otp-form data-otp-seconds={toMachineNumber(remaining)} className="mt-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <AuthButton
          type="button"
          tone="ghost"
          size="sm"
          className="w-auto"
          disabled={barred || pending || !online}
          onClick={onResend}
        >
          {online ? copy.actions.resend : copy.actions.offline}
        </AuthButton>

        <span className="text-xs text-ink-muted">
          {copy.timer.prefix}
          <bdi className="[font-variant-numeric:tabular-nums]">{toArabicDigits(String(remaining))}</bdi>
          {copy.timer.suffix}
        </span>
      </div>

      <p className="sr-only" aria-live="polite">
        {announcement}
      </p>
    </div>
  );
}
